import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { format } from 'date-fns';
import type { Variants } from 'framer-motion';
import { motion } from 'framer-motion';
import millify from 'millify';
import { twMerge } from 'tailwind-merge';
import { faAngleDown } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { Badge } from '@components/Song/Audience/Seat';
import type { AudienceSortType } from '@components/Song/Audience/constants';
import { Fade } from '@components/Song/shared';
import { Avatar } from '@components/basics/Images/Avatar';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { VerifiedTooltip } from '@components/main-components/tooltip/VerifiedTooltip';
import { useMediaQuery } from '@context/mediaQuery';
import { SPACE } from '@utils/constants';
import { getSingularOrPlural } from '@utils/helpers';
import { webappUriPath } from '@utils/url';

const MOBILE_CARD_HEIGHT_AUDIENCE = 100;

const MAX_COMMENT_SHOW_LENGTH_MOBILE = 45;
const MAX_COMMENT_SHOW_LENGTH_NEW_WAVEFORM = 74;

const AVATAR_SIZE = 48;

export function AudienceCommentCard({
  username,
  publicAddress,
  comment,
  avatarUrl,
  date,
  isArtist,
  webappUri,
  totalEditions,
  edition,
  sort,
  className,
}: {
  username: string;
  publicAddress: string;
  comment: string | undefined;
  avatarUrl: string | undefined;
  isArtist: boolean;
  date: string;
  webappUri: string;
  totalEditions?: number;
  edition: 'vip' | 'ga' | 'goldenEgg';
  sort: AudienceSortType;
  className?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const { tablet } = useMediaQuery();

  const maxCommentHeight = MOBILE_CARD_HEIGHT_AUDIENCE;

  const MAX_COMMENT_SHOW_LENGTH = !tablet
    ? MAX_COMMENT_SHOW_LENGTH_MOBILE
    : MAX_COMMENT_SHOW_LENGTH_NEW_WAVEFORM;

  const hide = !!comment && comment.length > MAX_COMMENT_SHOW_LENGTH;

  const animationVariants: Variants = {
    expand: {
      height: 'auto',
      transition: { duration: 0.2, delay: 0.1 },
    },
    collapse: {
      height: hide ? maxCommentHeight : 'auto',
      transition: { duration: 0.3, ease: 'easeOut' },
    },
  };

  const animationProps = hide
    ? {
        initial: 'collapse',
        animate: expanded ? 'expand' : 'collapse',
        variants: animationVariants,
        transition: { ease: 'easeOut' },
      }
    : null;

  const isLimitedEdition = edition === 'vip';
  const isGoldenEgg = edition === 'goldenEgg';

  return (
    <motion.div
      {...animationProps}
      className={twMerge(
        'relative flex h-auto w-full max-w-[255px] items-center justify-center gap-[10px] overflow-hidden rounded-lg bg-neutral900 py-3 pl-3 pr-4',
        className,
      )}
    >
      <div
        className={clsx(
          'relative flex h-12 w-12 flex-shrink-0 rounded-full',
          isLimitedEdition
            ? 'limited-border p-[2px]'
            : isGoldenEgg
            ? 'golden-egg-border p-[2px]'
            : '',
          !!hide && 'self-start',
        )}
      >
        {edition !== 'ga' && <Badge hasGoldenEgg={isGoldenEgg} type="hover_card" />}
        <Avatar
          size={AVATAR_SIZE}
          src={avatarUrl}
          username={username}
          borderRadius="50%"
          walletAddress={publicAddress}
          webappUri={webappUri}
        />
      </div>

      <motion.div className="group flex h-auto w-full flex-col gap-1 overflow-hidden">
        <AccessibleLink pathnameUri={webappUriPath(webappUri)}>
          <div className="flex items-center gap-1">
            <h2 className="overflow-hidden text-ellipsis whitespace-nowrap font-base text-base-m font-semibold text-white">
              {username}
            </h2>

            {isArtist && <VerifiedTooltip noTooltip size={14} />}
          </div>
        </AccessibleLink>

        <span className="font-base text-base-s font-medium text-base400">
          {sort === 'top' ? (
            <>
              {typeof totalEditions === 'number' && (
                <>
                  {millify(totalEditions)}
                  &nbsp;
                  {getSingularOrPlural({
                    word: 'edition',
                    count: totalEditions,
                  })}
                </>
              )}
              {edition !== 'ga' && typeof totalEditions === 'number' && (
                <>
                  {SPACE}•{SPACE}
                </>
              )}
              {edition !== 'ga' && (
                <>
                  <span
                    className={clsx(
                      'text-gradient-animated bg-clip-text',
                      isLimitedEdition ? 'limited-text' : 'golden-egg-text',
                    )}
                    style={{
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    {isLimitedEdition ? 'Limited Edition' : 'Golden Egg'}
                  </span>
                </>
              )}
            </>
          ) : (
            format(new Date(date), 'P')
          )}
        </span>

        {!!comment && (
          <p
            className={clsx(
              'h-auto flex-grow overflow-hidden text-ellipsis font-base text-base-m text-white',
              !!hide && 'cursor-pointer',
              !expanded && 'max-h-9',
            )}
            onClick={() => setExpanded(prev => !prev)}
          >
            {comment}
          </p>
        )}

        {!!hide && !expanded && (
          <>
            <motion.div
              className="pointer-events-none absolute bottom-0 h-12 w-full"
              transition={{ duration: 0.2, delay: 0.1 }}
            >
              <Fade length={15} lengthStartColor="#171717" lengthEndColor="rgba(23, 23, 23, 0)" />
            </motion.div>
            <motion.div
              className="absolute -bottom-3 right-3 cursor-pointer py-3 pb-4 text-base500 transition-[filter] duration-300 group-hover:brightness-130"
              transition={{ duration: 0.2, delay: 0.1 }}
              onClick={e => {
                e.stopPropagation();
                setExpanded(prev => !prev);
              }}
            >
              <FontAwesomeIcon icon={faAngleDown} className="" />
            </motion.div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}
